import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SchedulesService, Schedule, ExcludedDay, TimeSlot as ApiTimeSlot, Appointment, AppointmentsDashboard, AppointmentHistoryEntry } from '../../services/schedules.service';
import { CustomersService } from '../../services/customers.service';
import { Customer } from '../../models/Customer';
import { TableAction } from '../../shared/components/table/table.component';
import { AuthService } from '../../services/auth.service';
import { PermissionService, ModuleGroup } from '../../services/permission.service';
import { DashComponentConfig, DashCard, DashChart } from '../../shared/interfaces/dash-component.interface';
import { ExternalLinksService, ExternalLinkCreate } from '../../services/external-links.service';
import { ExternalServiceProvidersService } from '../../services/external-service-providers.service';
import { ExternalServiceProvider } from '../../models/ExternalServiceProvider';
import { ConfirmationService } from '../../shared/services/confirmation.service';
import { B2bAppointmentBookingService, resolveAppointmentPlate } from './b2b-booking/b2b-appointment-booking.service';
import { switchMap, map, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import {
  datetimeLocalToRecordedAtIso,
  defaultDatetimeLocalValue
} from '../../shared/utils/action-recorded-at.util';

type Reason = 'holiday' | 'excluded' | 'weekday' | undefined;

interface TimeSlot {
  start: string;
  end: string;
  capacity?: number;
  bookedCount?: number;
  totalCapacity?: number;
}

interface CalendarDay {
  dayName: string;
  dayNumber: number;
  monthName: string;
  monthShort: string;
  fullDate: Date;
  available: boolean;
  reason?: Reason;
  timeSlots: TimeSlot[];
  appointmentsCount: number;
}

@Component({
  selector: 'app-appointments',
  templateUrl: './appointments.component.html',
  styleUrls: ['./appointments.component.css'],
  providers: [B2bAppointmentBookingService]
})
export class AppointmentsComponent implements OnInit, OnDestroy {
  groupIdFilter: number | null = null;
  /** Quando true, restringe a tela aos agendamentos cujas agendas tenham CanExternalLink = true (visão B2B). */
  b2bOnly: boolean = false;

  constructor(
    private schedulesService: SchedulesService,
    private customersService: CustomersService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private permissionService: PermissionService,
    private externalLinksService: ExternalLinksService,
    private externalServiceProvidersService: ExternalServiceProvidersService,
    private confirmationService: ConfirmationService,
    public b2bBooking: B2bAppointmentBookingService
  ) {}

  private showInfoMessage(message: string, title: string = 'Informação', type: 'info' | 'warning' | 'danger' | 'question' = 'info'): void {
    this.confirmationService.confirm({
      title,
      message,
      type,
      confirmText: 'OK',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'd-none'
    }).subscribe();
  }

  previewMode: 'monthly' | 'weekly' | 'dashboard' = 'monthly';
  previewDays: CalendarDay[] = [];
  weeklyDays: CalendarDay[] = [];
  weeklyStartDate!: Date;

  // Navegação mensal
  viewYear!: number;
  viewMonth!: number; // 0-11
  yearsList: number[] = [];

  selectedPreviewDay?: CalendarDay;
  selectedTimeSlot?: TimeSlot;
  showModal = false;
  showTimeSlots = false;
  showSuccessModal = false;
  showNewAppointmentSection = false;
  appointmentsCountByDate: { [date: string]: number } = {};

  attendancesPerSlot = 1;
  selectedCustomer: Customer | null = null;
  scheduleSuccess = false;
  scheduleError: string | null = null;

  // Clientes
  customers: Customer[] = [];
  loadingCustomers = false;
  showCustomerTable = false;
  creatingAppointment = false;

  // Configuração da tabela de clientes
  customerTableTitle = 'Selecione um Cliente';
  customerColumns = [
    { titulo: 'Razão Social', label: 'name' },
    { titulo: 'Documento', label: 'documentNumber' },
    { titulo: 'Email', label: 'email' },
    { titulo: 'Telefone', label: 'primaryPhone' }
  ];
  customerTableActions: TableAction[] = [];

  // Agendas
  schedules: Schedule[] = [];
  selectedSchedule?: Schedule;
  selectedScheduleId: number | null = null;
  loadingSchedules = false;
  schedulesError: string | null = null;

  // Busca por CPF/CNPJ
  showSearchModal = false;
  documentSearchTerm = '';
  searchingByDocument = false;
  searchByDocumentError: string | null = null;
  searchByDocumentResults: Appointment[] = [];
  private pendingSearchNavigationDate: Date | null = null;

  // Dias excluídos e loading
  excludedDays: ExcludedDay[] = [];
  loadingExcludedDays = false;
  loadingDayAvailability = false;
  showCalendar = false;

  // Dashboard
  dashboardLoading = false;
  dashboardError: string | null = null;
  dashConfig?: DashComponentConfig;

  // Appointments do dia
  dayAppointments: Appointment[] = [];
  loadingAppointments = false;
  deletingAppointmentId: number | null = null;
  updatingStatusAppointmentId: number | null = null;
  availableStatuses = ['Agendado', 'Confirmado', 'Compareceu', 'Não compareceu', 'Cancelado'];
  showStatusSelectionModal = false;
  selectedAppointmentForStatus: Appointment | null = null;
  
  // Modal de motivo de cancelamento
  showCancelReasonModal = false;
  cancelReason = '';
  cancelReasonError = '';
  pendingStatusUpdate: { appointment: Appointment, status: string } | null = null;
  /** cancel = status genérico; reject-partner = rejeitar solicitação de oficina parceira */
  cancelReasonMode: 'cancel' | 'reject-partner' = 'cancel';

  // Modal de data/hora do comparecimento (ou falta)
  showAttendanceRecordedAtModal = false;
  attendanceRecordedAt = '';
  attendanceRecordedAtError = '';
  pendingAttendanceUpdate: { appointment: Appointment; status: string } | null = null;

  // Fila B2B: agendamentos em parceiro aguardando análise interna
  partnerPendingAppointments: Appointment[] = [];
  loadingPartnerPending = false;
  partnerPendingError: string | null = null;
  partnerActionId: number | null = null;

  /** Aba da fila operacional: parceiros (aguardando) vs solicitações de guincho (centralizadas). */
  opsQueueTab: 'parceiros' | 'guincho' = 'parceiros';
  guinchoRequests: Appointment[] = [];
  loadingGuinchoRequests = false;
  guinchoRequestsError: string | null = null;
  includeClosedGuincho = false;

  appointmentHistory: AppointmentHistoryEntry[] = [];
  loadingAppointmentHistory = false;

  // Reagendar data/hora de solicitação em oficina parceira
  showPartnerRescheduleModal = false;
  partnerRescheduleApp: Appointment | null = null;
  partnerRescheduleDate = '';
  partnerRescheduleStart = '09:00';
  partnerRescheduleEnd = '09:30';
  partnerRescheduleAndConfirm = false;
  partnerRescheduleError = '';
  partnerRescheduleMinDate = '';
  partnerRescheduleProviderId: number | null = null;
  partnerRescheduleProviders: ExternalServiceProvider[] = [];
  partnerRescheduleProvidersLoading = false;
  partnerRescheduleVehicleUf = '';

  readonly partnerTimeChoices: string[] = (() => {
    const out: string[] = [];
    for (let m = 8 * 60; m < 18 * 60; m += 30) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      out.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    }
    return out;
  })();

  // Link Externo
  showExternalLinkModal = false;
  generatedExternalLink: string | null = null;
  generatingLink = false;
  linkSelectedCustomerId: number | null = null;
  linkSelectedProviderId: number | null = null;
  providers: ExternalServiceProvider[] = [];
  loadingProviders = false;

  // Modal de detalhes do agendamento
  showAppointmentDetailsModal = false;
  selectedAppointmentDetails: Appointment | null = null;
  
  // Paginação dos appointments
  appointmentsPage = 1;
  appointmentsPageSize = 5;
  Math = Math; // Expor Math para o template
  get paginatedAppointments(): Appointment[] {
    const start = (this.appointmentsPage - 1) * this.appointmentsPageSize;
    const end = start + this.appointmentsPageSize;
    return this.dayAppointments.slice(start, end);
  }
  get totalAppointmentsPages(): number {
    return Math.ceil(this.dayAppointments.length / this.appointmentsPageSize);
  }

  ngOnInit(): void {
    const today = new Date();
    this.viewYear = today.getFullYear();
    this.viewMonth = today.getMonth();
    this.initYearsList();

    const groupIdParam = this.route.snapshot.queryParamMap.get('groupId');
    const b2bOnlyParam = this.route.snapshot.queryParamMap.get('b2bOnly');
    const isB2bRoute = (this.route.snapshot.data && (this.route.snapshot.data as any)['b2bOnly']) === true;
    this.b2bOnly = isB2bRoute || b2bOnlyParam === 'true' || b2bOnlyParam === '1';
    const hasAdmin = this.permissionService.hasGroup(ModuleGroup.Administrativo);

    if (this.b2bOnly) {
      // Visão B2B: requer acesso ao grupo B2B (ou Administrativo)
      if (!hasAdmin && !this.permissionService.hasGroup(ModuleGroup.B2B)) {
        this.router.navigate(['/dashboard']);
        return;
      }
    } else if (groupIdParam) {
      const requestedGroupId = Number(groupIdParam);
      // Garante que o usuário realmente tem acesso ao grupo solicitado
      if (!this.permissionService.hasGroup(requestedGroupId)) {
        this.router.navigate(['/dashboard']);
        return;
      }
      this.groupIdFilter = requestedGroupId;
    } else {
      // Sem filtro de grupo: apenas Administrativo pode ver todos
      if (!hasAdmin) {
        // Verifica se tem acesso ao Comercial e redireciona para a visão filtrada
        if (this.permissionService.hasGroup(ModuleGroup.Commercial)) {
          this.router.navigate(['/appointments'], { queryParams: { groupId: ModuleGroup.Commercial } });
        } else {
          this.router.navigate(['/dashboard']);
        }
        return;
      }
    }

    this.loadSchedules();
    if (this.b2bOnly) {
      // Sem spinner próprio na entrada: agendas já têm loadingSchedules.
      this.loadPartnerPendingAppointments({ showSpinner: false });
      this.loadGuinchoRequests({ showSpinner: false });
    }
  }

  loadPartnerPendingAppointments(options?: { showSpinner?: boolean }): void {
    if (!this.b2bOnly) return;
    const showSpinner = options?.showSpinner !== false;
    if (showSpinner) {
      this.loadingPartnerPending = true;
    }
    this.partnerPendingError = null;
    this.schedulesService.getPartnerPendingAppointments().subscribe({
      next: (list) => {
        // Fila de confirmação: parceiros + base B2B (AguardandoAnalise). Guincho na aba dedicada.
        this.partnerPendingAppointments = (list || []).filter(a =>
          !a.IsDeleted
          && !this.isGuinchoSolicitacao(a)
          && (
            (a.ExternalServiceProviderId ?? 0) > 0
            || (a.B2BContractId ?? 0) > 0
            || (a.ScheduleId ?? 0) > 0
          )
        );
        if (showSpinner) {
          this.loadingPartnerPending = false;
        }
      },
      error: (err) => {
        console.error('❌ Erro ao carregar fila de análise parceiros:', err);
        this.partnerPendingError = 'Não foi possível carregar solicitações de oficinas parceiras.';
        this.partnerPendingAppointments = [];
        if (showSpinner) {
          this.loadingPartnerPending = false;
        }
      }
    });
  }

  loadGuinchoRequests(options?: { showSpinner?: boolean }): void {
    if (!this.b2bOnly) return;
    const showSpinner = options?.showSpinner !== false;
    if (showSpinner) this.loadingGuinchoRequests = true;
    this.guinchoRequestsError = null;
    this.schedulesService.getGuinchoRequests(this.includeClosedGuincho).subscribe({
      next: (list) => {
        this.guinchoRequests = (list || []).filter(a => !a.IsDeleted);
        if (showSpinner) this.loadingGuinchoRequests = false;
      },
      error: () => {
        this.guinchoRequestsError = 'Não foi possível carregar solicitações de guincho.';
        this.guinchoRequests = [];
        if (showSpinner) this.loadingGuinchoRequests = false;
      }
    });
  }

  selectOpsQueueTab(tab: 'parceiros' | 'guincho'): void {
    this.opsQueueTab = tab;
    if (tab === 'guincho') this.loadGuinchoRequests();
    else this.loadPartnerPendingAppointments();
  }

  get partnerPendingNewCount(): number {
    return this.partnerPendingAppointments.filter(a => this.isNewAppointment(a)).length;
  }

  get guinchoNewCount(): number {
    return this.guinchoRequests.filter(a => this.isNewAppointment(a)).length;
  }

  isNewAppointment(app: Appointment): boolean {
    const raw = app.CreatedAt;
    if (!raw) return false;
    const created = new Date(raw).getTime();
    if (!Number.isFinite(created)) return false;
    return (Date.now() - created) < 24 * 60 * 60 * 1000;
  }

  providerNameOf(app: Appointment): string {
    if (this.isGuinchoSolicitacao(app)) {
      return 'Guincho (triagem)';
    }
    if ((app.ExternalServiceProviderId ?? 0) > 0) {
      return app.ExternalServiceProviderName
        || this.providers.find(p => p.id === app.ExternalServiceProviderId)?.tradeName
        || `Parceiro #${app.ExternalServiceProviderId}`;
    }
    // Base B2B aguardando confirmação
    const scheduleName = this.schedules.find(s => s.Id === app.ScheduleId)?.Name;
    return scheduleName ? `Base · ${scheduleName}` : 'Base B2B';
  }

  isBaseB2bPending(app: Appointment): boolean {
    return (app.ExternalServiceProviderId ?? 0) <= 0
      && !this.isGuinchoSolicitacao(app)
      && ((app.B2BContractId ?? 0) > 0 || (app.ScheduleId ?? 0) > 0);
  }

  isGuinchoSolicitacao(app: Appointment): boolean {
    if (app.IsGuinchoRequest) return true;
    const subject = (app.Subject || '').toLowerCase();
    const description = (app.Description || '').toLowerCase();
    return subject.includes('solicitação guincho')
      || subject.includes('solicitacao guincho')
      || (subject.includes('guincho') && subject.includes('portal'))
      || description.includes('solicitação urgente de guincho')
      || description.includes('solicitacao urgente de guincho');
  }

  isPartnerAppointment(app: Appointment): boolean {
    return (app.ExternalServiceProviderId ?? 0) > 0 || this.isGuinchoSolicitacao(app);
  }

  /** B2B: comparecimento / orçamento na base ou em oficina parceira. */
  isB2bAttendanceEligible(app: Appointment): boolean {
    return !!(this.selectedSchedule?.CanExternalLink || this.isPartnerAppointment(app));
  }

  /** Coluna oficina na lista diária (base B2B ou parceiros). */
  get showPartnerWorkshopColumn(): boolean {
    return this.b2bOnly || !!this.selectedSchedule?.CanExternalLink;
  }

  /** Após confirmar/reagendar parceiro: atualiza fila, calendário e abre o dia do agendamento. */
  private refreshAfterPartnerWorkflow(app: Appointment, confirmed: boolean, dateOverride?: string): void {
    this.loadPartnerPendingAppointments();
    this.loadGuinchoRequests({ showSpinner: false });
    if (this.selectedScheduleId) {
      this.loadScheduleAppointmentsCount(this.selectedScheduleId);
    }

    if (confirmed) {
      const dateIso = (dateOverride || String(app.Date || '')).split('T')[0];
      const targetDate = dateIso ? this.parseApiDate(dateIso) : null;
      if (targetDate) {
        this.focusCalendarOnDate(targetDate);
        return;
      }
    }

    if (this.selectedPreviewDay) {
      this.loadDayAppointments(this.selectedPreviewDay.fullDate);
    }
  }

  formatAppointmentDate(app: Appointment): string {
    const raw = (app.Date || '').toString();
    const day = raw.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
      const [y, m, d] = day.split('-');
      return `${d}/${m}/${y}`;
    }
    return day || '—';
  }

  formatAppointmentTime(value: string | null | undefined): string {
    const t = (value || '').toString();
    if (t.length >= 5) return t.slice(0, 5);
    return t || '—';
  }

  confirmPartnerBooking(app: Appointment): void {
    if (!app.Id) return;
    const isBase = this.isBaseB2bPending(app);
    this.confirmationService.confirm({
      title: isBase ? 'Confirmar agendamento da base' : 'Confirmar agendamento parceiro',
      message: isBase
        ? `Confirmar ${app.Plate || 'veículo'} na base (${this.providerNameOf(app)}) e liberar na data agendada?`
        : `Confirmar ${app.Plate || 'veículo'} em ${this.providerNameOf(app)} após contato com a oficina?`,
      confirmText: 'Confirmar',
      cancelText: 'Voltar',
      type: 'question',
      icon: 'fas fa-check-circle'
    }).subscribe(ok => {
      if (!ok) return;
      this.executePartnerAnalysis(app, 'Agendado');
    });
  }

  rejectPartnerBooking(app: Appointment): void {
    if (!app.Id) return;
    this.cancelReasonMode = 'reject-partner';
    this.pendingStatusUpdate = { appointment: app, status: 'Cancelado' };
    this.cancelReason = '';
    this.cancelReasonError = '';
    this.showCancelReasonModal = true;
  }

  openPartnerReschedule(app: Appointment): void {
    if (!app.Id) return;
    this.partnerRescheduleApp = app;
    this.partnerRescheduleError = '';
    this.partnerRescheduleAndConfirm = false;
    this.partnerRescheduleProviderId = app.ExternalServiceProviderId ?? null;
    this.partnerRescheduleProviders = [];
    this.partnerRescheduleVehicleUf = '';

    const today = new Date();
    this.partnerRescheduleMinDate = this.toDateInputValue(today);

    const raw = (app.Date || '').toString().slice(0, 10);
    this.partnerRescheduleDate = /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? raw
      : this.partnerRescheduleMinDate;

    const start = this.formatAppointmentTime(app.StartTime);
    const end = this.formatAppointmentTime(app.EndTime);
    this.partnerRescheduleStart = this.partnerTimeChoices.includes(start) ? start : '09:00';
    this.partnerRescheduleEnd = (end && end > this.partnerRescheduleStart)
      ? end
      : this.nextPartnerHalfHour(this.partnerRescheduleStart);

    this.showPartnerRescheduleModal = true;
    this.loadPartnerRescheduleProviders(app);
  }

  /** Atalho: abre o mesmo modal de reagendamento (permite trocar oficina). */
  openPartnerReassign(app: Appointment): void {
    this.openPartnerReschedule(app);
  }

  closePartnerRescheduleModal(): void {
    if (this.partnerActionId != null) return;
    this.showPartnerRescheduleModal = false;
    this.partnerRescheduleApp = null;
    this.partnerRescheduleError = '';
    this.partnerRescheduleAndConfirm = false;
    this.partnerRescheduleProviderId = null;
    this.partnerRescheduleProviders = [];
    this.partnerRescheduleProvidersLoading = false;
    this.partnerRescheduleVehicleUf = '';
  }

  private loadPartnerRescheduleProviders(app: Appointment): void {
    this.partnerRescheduleProvidersLoading = true;
    this.partnerRescheduleError = '';
    this.externalServiceProvidersService.resolveVehicleState({
      plate: app.Plate,
      contractId: app.B2BContractId,
      vehicleId: app.VehicleId,
    }).pipe(
      switchMap(uf => {
        this.partnerRescheduleVehicleUf = (uf || '').trim().toUpperCase();
        return this.externalServiceProvidersService.list(
          1,
          200,
          undefined,
          this.partnerRescheduleVehicleUf || undefined,
        );
      }),
      switchMap(res => of(res.items || []))
    ).subscribe({
      next: (items) => {
        this.partnerRescheduleProvidersLoading = false;
        this.partnerRescheduleProviders = items;
        const currentId = app.ExternalServiceProviderId ?? 0;
        const stillValid = items.some(p => p.id === this.partnerRescheduleProviderId);
        if (!stillValid && currentId > 0 && items.some(p => p.id === currentId)) {
          this.partnerRescheduleProviderId = currentId;
        } else if (!stillValid && items.length > 0) {
          this.partnerRescheduleProviderId = items[0].id;
        }
        if (items.length === 0) {
          this.partnerRescheduleError = this.partnerRescheduleVehicleUf
            ? `Nenhuma oficina atende a UF ${this.partnerRescheduleVehicleUf}.`
            : 'Não foi possível determinar a UF do veículo ou não há parceiros disponíveis.';
        }
      },
      error: () => {
        this.partnerRescheduleProvidersLoading = false;
        this.partnerRescheduleError = 'Não foi possível carregar oficinas parceiras para a região do veículo.';
      }
    });
  }

  private selectedRescheduleProvider(): ExternalServiceProvider | null {
    return this.partnerRescheduleProviders.find(p => p.id === this.partnerRescheduleProviderId) ?? null;
  }

  onPartnerRescheduleStartChange(): void {
    if (!this.partnerRescheduleStart) return;
    if (!this.partnerRescheduleEnd || this.partnerRescheduleEnd <= this.partnerRescheduleStart) {
      this.partnerRescheduleEnd = this.nextPartnerHalfHour(this.partnerRescheduleStart);
    }
  }

  savePartnerReschedule(): void {
    const app = this.partnerRescheduleApp;
    if (!app?.Id) return;

    if (!this.partnerRescheduleProviderId || this.partnerRescheduleProviderId <= 0) {
      this.partnerRescheduleError = 'Selecione a oficina parceira.';
      return;
    }
    if (!this.partnerRescheduleDate) {
      this.partnerRescheduleError = 'Informe a nova data.';
      return;
    }
    if (!this.partnerRescheduleStart || !this.partnerRescheduleEnd) {
      this.partnerRescheduleError = 'Informe horário de início e fim.';
      return;
    }
    if (this.partnerRescheduleStart >= this.partnerRescheduleEnd) {
      this.partnerRescheduleError = 'Horário final deve ser depois do início.';
      return;
    }

    const currentUser = this.authService.currentUserValue;
    const userName = currentUser?.username || 'Sistema';
    const newStatus = this.partnerRescheduleAndConfirm ? 'Agendado' : (app.Status || 'AguardandoAnalise');
    const selected = this.selectedRescheduleProvider();
    const toName = selected?.tradeName || `Parceiro #${this.partnerRescheduleProviderId}`;
    const fromId = app.ExternalServiceProviderId ?? 0;
    const partnerChanged = this.partnerRescheduleProviderId !== fromId;
    const scheduleLabel = `${this.partnerRescheduleDate} ${this.partnerRescheduleStart}-${this.partnerRescheduleEnd}`;

    let description: string;
    if (partnerChanged) {
      const fromName = this.providerNameOf(app);
      description = this.partnerRescheduleAndConfirm
        ? `(Reagendado e confirmado: ${fromName} → ${toName}, ${scheduleLabel}) - ${userName}`
        : `(Reagendado com troca de parceiro: ${fromName} → ${toName}, ${scheduleLabel}) - ${userName}`;
    } else {
      description = this.partnerRescheduleAndConfirm
        ? `(Reagendado e confirmado análise parceiro: ${scheduleLabel}) - ${userName}`
        : `(Reagendado análise parceiro: ${scheduleLabel}) - ${userName}`;
    }

    this.partnerRescheduleError = '';
    this.partnerActionId = app.Id;

    const updatedAppointment: Appointment = {
      ...app,
      Date: `${this.partnerRescheduleDate}T00:00:00`,
      StartTime: `${this.partnerRescheduleStart}:00`,
      EndTime: `${this.partnerRescheduleEnd}:00`,
      ExternalServiceProviderId: this.partnerRescheduleProviderId,
      ExternalServiceProviderName: toName,
      Status: newStatus,
      Description: description,
      Subject: app.Subject?.includes('Parceiro') ? `Parceiro · ${toName}` : (app.Subject || `Parceiro · ${toName}`),
      UpdatedBy: userName
    };

    this.schedulesService.updateAppointment(app.Id, updatedAppointment).subscribe({
      next: () => {
        this.partnerActionId = null;
        this.showPartnerRescheduleModal = false;
        this.partnerRescheduleApp = null;
        this.refreshAfterPartnerWorkflow(
          updatedAppointment,
          this.partnerRescheduleAndConfirm,
          this.partnerRescheduleDate
        );
      },
      error: (err) => {
        console.error('❌ Erro ao reagendar parceiro:', err);
        this.partnerActionId = null;
        this.partnerRescheduleError = 'Não foi possível salvar o reagendamento. Tente novamente.';
      }
    });
  }

  private toDateInputValue(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private nextPartnerHalfHour(start: string): string {
    const [hh, mm] = start.split(':').map(Number);
    let total = (hh || 0) * 60 + (mm || 0) + 30;
    if (total > 18 * 60) total = 18 * 60;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  private executePartnerAnalysis(app: Appointment, newStatus: 'Agendado' | 'Cancelado', reason = ''): void {
    const currentUser = this.authService.currentUserValue;
    const userName = currentUser?.username || 'Sistema';
    this.partnerActionId = app.Id!;

    const notes = newStatus === 'Cancelado' && reason.trim()
      ? (app.PartnerNotes
          ? `${app.PartnerNotes}\n[Recusa ERP]: ${reason.trim()}`
          : reason.trim())
      : (app.PartnerNotes || null);

    const description = newStatus === 'Cancelado'
      ? `(Rejeitado análise parceiro: ${reason.trim()}) - ${userName}`
      : `(Confirmado análise parceiro) - ${userName}`;

    const updatedAppointment: Appointment = {
      ...app,
      Status: newStatus,
      PartnerNotes: notes,
      Description: description,
      UpdatedBy: userName
    };

    this.schedulesService.updateAppointment(app.Id!, updatedAppointment).subscribe({
      next: () => {
        this.partnerActionId = null;
        this.refreshAfterPartnerWorkflow(app, newStatus === 'Agendado');
      },
      error: (err) => {
        console.error('❌ Erro ao atualizar análise parceiro:', err);
        this.partnerActionId = null;
        alert('Não foi possível atualizar o agendamento parceiro.');
      }
    });
  }

  private loadSchedules(): void {
    this.loadingSchedules = true;
    this.schedulesError = null;
    this.schedulesService.getAll().subscribe({
      next: (list) => {
        let normalized = list.map(s => ({
          ...s,
          CanExternalLink: !!s.CanExternalLink
        }));
        // Visão B2B: somente agendas que permitem agendamento externo
        if (this.b2bOnly) {
          normalized = normalized.filter(s => s.CanExternalLink === true);
        }
        this.schedules = normalized;
        this.loadingSchedules = false;

        // B2B: já seleciona a agenda disponível (geralmente única) e carrega o calendário.
        if (this.b2bOnly && this.schedules.length > 0 && this.selectedScheduleId == null) {
          const first = this.schedules[0];
          const firstId = first?.Id != null ? Number(first.Id) : NaN;
          if (!Number.isNaN(firstId) && firstId > 0) {
            this.selectedScheduleId = firstId;
            this.onScheduleChange(firstId);
            return;
          }
        }

        this.tryHandlePendingSearchNavigation();
      },
      error: (err) => {
        console.error('❌ Erro ao carregar agendas:', err);
        this.schedulesError = 'Não foi possível carregar as agendas.';
        this.loadingSchedules = false;
      }
    });
  }

  onScheduleChange(scheduleId: number | null): void {
    const idNum = scheduleId != null ? Number(scheduleId) : NaN;
    this.selectedSchedule = this.schedules.find(s => Number(s.Id) === idNum);
    
    if (!this.selectedSchedule) {
      this.showCalendar = false;
      return;
    }

    // Defesa em profundidade: na visão B2B só é permitido operar agendas com CanExternalLink = true.
    if (this.b2bOnly && !this.selectedSchedule.CanExternalLink) {
      this.showInfoMessage(
        'Esta agenda não permite agendamento externo e não pode ser utilizada na visão B2B.',
        'Acesso bloqueado',
        'warning'
      );
      this.selectedSchedule = undefined;
      this.selectedScheduleId = null;
      this.showCalendar = false;
      return;
    }
    
    if (this.selectedSchedule?.AppointmentsByHours) {
      this.attendancesPerSlot = this.selectedSchedule.AppointmentsByHours;
    }
    
    // Carregar dias excluídos antes de construir o calendário
    this.loadExcludedDaysForSchedule(idNum);
    this.loadScheduleAppointmentsCount(idNum);

    // Se estiver em modo dashboard, carregar dados
    if (this.previewMode === 'dashboard') {
      this.loadDashboard();
    }
  }

  openSearchModal(): void {
    this.showSearchModal = true;
    this.searchByDocumentError = null;
    this.searchByDocumentResults = [];
  }

  closeSearchModal(): void {
    this.showSearchModal = false;
    this.searchingByDocument = false;
  }

  searchAppointmentsByDocument(): void {
    const cleanDocument = (this.documentSearchTerm || '').replace(/\D/g, '');

    if (!cleanDocument || (cleanDocument.length !== 11 && cleanDocument.length !== 14)) {
      this.searchByDocumentError = 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.';
      this.searchByDocumentResults = [];
      return;
    }

    this.searchingByDocument = true;
    this.searchByDocumentError = null;
    this.searchByDocumentResults = [];

    this.schedulesService.getAppointmentsByDocument(cleanDocument, 1, 100).subscribe({
      next: (appointments) => {
        this.searchByDocumentResults = (appointments || []).filter(a => !a.IsDeleted);

        if (!this.searchByDocumentResults.length) {
          this.searchByDocumentError = 'Nenhum agendamento encontrado para este CPF/CNPJ.';
        }

        this.searchingByDocument = false;
      },
      error: (err) => {
        console.error('❌ Erro ao pesquisar agendamentos por documento:', err);
        this.searchByDocumentError = 'Não foi possível buscar agendamentos por CPF/CNPJ.';
        this.searchingByDocument = false;
      }
    });
  }

  goToAppointmentDay(appointment: Appointment): void {
    const targetDate = this.parseApiDate(appointment.Date);
    const appointmentScheduleId = Number(appointment.ScheduleId);

    if (!targetDate || !appointmentScheduleId) {
      this.searchByDocumentError = 'Não foi possível identificar a data do agendamento.';
      return;
    }

    if (this.selectedScheduleId !== appointmentScheduleId) {
      this.pendingSearchNavigationDate = targetDate;
      this.selectedScheduleId = appointmentScheduleId;
      this.onScheduleChange(appointmentScheduleId);
      return;
    }

    this.focusCalendarOnDate(targetDate);
  }

  private loadExcludedDaysForSchedule(scheduleId: number): void {
    this.loadingExcludedDays = true;
    this.showCalendar = false;
    this.excludedDays = [];

    this.schedulesService.getAllExcludedDays().subscribe({
      next: (allExcluded) => {
        // Filtrar apenas os dias excluídos desta agenda
        this.excludedDays = allExcluded.filter(ed => ed.ScheduleId === scheduleId);
        this.loadingExcludedDays = false;
        this.showCalendar = true;
        
        // Agora construir o calendário
        this.buildMonthlyPreview();
        this.initWeeklyView();
        this.tryHandlePendingSearchNavigation();
      },
      error: (err) => {
        console.error('❌ Erro ao carregar dias excluídos:', err);
        this.loadingExcludedDays = false;
        // Mesmo com erro, mostrar o calendário
        this.showCalendar = true;
        this.buildMonthlyPreview();
        this.initWeeklyView();
        this.tryHandlePendingSearchNavigation();
      }
    });
  }

  private loadScheduleAppointmentsCount(scheduleId: number): void {
    this.appointmentsCountByDate = {};
    const range = this.getPreviewDateRange();

    const schedule$ = this.schedulesService.getAppointmentsByScheduleId(scheduleId).pipe(
      catchError(err => {
        console.error('❌ Erro ao carregar contagem de agendamentos:', err);
        return of([] as Appointment[]);
      })
    );

    const partners$ = range
      ? this.schedulesService.getPartnerAppointmentsByRange(range.from, range.to).pipe(
          catchError(err => {
            console.error('❌ Erro ao carregar contagem de parceiros:', err);
            return of([] as Appointment[]);
          })
        )
      : of([] as Appointment[]);

    forkJoin({ schedule: schedule$, partners: partners$ }).subscribe({
      next: ({ schedule, partners }) => {
        const countMap: { [date: string]: number } = {};
        const seen = new Set<number>();

        [...(schedule || []), ...(partners || [])].forEach(app => {
          if (app.IsDeleted) return;
          if (app.Id && seen.has(app.Id)) return;
          if (app.Id) seen.add(app.Id);

          const dateKey = String(app.Date || '').split('T')[0];
          if (!dateKey) return;
          countMap[dateKey] = (countMap[dateKey] || 0) + 1;
        });

        this.appointmentsCountByDate = countMap;
        this.refreshCalendarWithAppointmentsCount();
      }
    });
  }

  private getPreviewDateRange(): { from: string; to: string } | null {
    const days = [...this.previewDays, ...this.weeklyDays];
    if (!days.length) return null;

    const times = days.map(d => d.fullDate.getTime());
    const min = new Date(Math.min(...times));
    const max = new Date(Math.max(...times));
    return { from: this.formatDateToISO(min), to: this.formatDateToISO(max) };
  }

  private refreshCalendarWithAppointmentsCount(): void {
    this.previewDays = this.previewDays.map(day => ({
      ...day,
      appointmentsCount: this.getAppointmentsCountForDate(day.fullDate)
    }));

    this.weeklyDays = this.weeklyDays.map(day => ({
      ...day,
      appointmentsCount: this.getAppointmentsCountForDate(day.fullDate)
    }));

    if (this.selectedPreviewDay) {
      this.selectedPreviewDay.appointmentsCount = this.getAppointmentsCountForDate(this.selectedPreviewDay.fullDate);
    }
  }

  private getAppointmentsCountForDate(date: Date): number {
    const dateKey = this.formatDateToISO(date);
    return this.appointmentsCountByDate[dateKey] || 0;
  }

  onPreviewModeChange(): void {
    if (this.previewMode === 'weekly') {
      this.initWeeklyView();
    } else if (this.previewMode === 'dashboard') {
      this.loadDashboard();
    }
  }

  selectPreviewDay(day: CalendarDay): void {
    this.selectedPreviewDay = day;
    this.selectedTimeSlot = undefined;
    this.scheduleSuccess = false;
    this.showTimeSlots = true;
    this.showNewAppointmentSection = false;
    this.showCalendar = false;
    this.dayAppointments = [];
    this.appointmentsPage = 1;

    // Carregar appointments do dia independente de estar disponível ou não
    this.loadDayAppointments(day.fullDate);

    if (!day.available) {
      this.loadingDayAvailability = false;
      return;
    }

    // Se o dia está disponível, verificar vagas existentes
    this.loadingDayAvailability = true;

    if (!this.selectedScheduleId) {
      this.loadingDayAvailability = false;
      return;
    }

    const dateStr = this.formatDateToISO(day.fullDate);
    
    this.schedulesService.getAllTimeSlots(this.selectedScheduleId, dateStr).subscribe({
      next: (existingSlots) => {
        
        if (existingSlots && existingSlots.length > 0) {
          // Atualizar os slots com a capacidade real
          day.timeSlots = day.timeSlots.map(generatedSlot => {
            // Procurar o slot correspondente na API
            const matchingSlot = existingSlots.find(apiSlot => {
              // Comparar horários normalizando o formato (com ou sem segundos)
              const slotStart = apiSlot.StartTime.substring(0, 5);
              const slotEnd = apiSlot.EndTime.substring(0, 5);
              return slotStart === generatedSlot.start && slotEnd === generatedSlot.end;
            });
            
            if (matchingSlot) {
              const availableCapacity = matchingSlot.Capacity - matchingSlot.BookedCount;
              return {
                ...generatedSlot,
                capacity: availableCapacity,
                bookedCount: matchingSlot.BookedCount,
                totalCapacity: matchingSlot.Capacity
              };
            }
            
            return generatedSlot;
          });
          
          // Filtrar apenas os slots com capacidade disponível
          day.timeSlots = day.timeSlots.filter(slot => 
            slot.capacity === undefined || slot.capacity > 0
          );
          
          // Se não há mais vagas, marcar o dia como indisponível
          if (day.timeSlots.length === 0) {
            day.available = false;
            day.reason = 'excluded';
          }
        }
        
        this.loadingDayAvailability = false;
      },
      error: (err) => {
        console.error('❌ Erro ao verificar disponibilidade:', err);
        this.loadingDayAvailability = false;
      }
    });
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedPreviewDay = undefined;
    this.selectedTimeSlot = undefined;
    this.selectedCustomer = null;
    this.scheduleSuccess = false;
    this.b2bBooking.reset();
  }

  backToCalendar(): void {
    this.showTimeSlots = false;
    this.showCalendar = true;
    this.showNewAppointmentSection = false;
    this.selectedPreviewDay = undefined;
    this.selectedTimeSlot = undefined;
    this.selectedCustomer = null;
    this.scheduleSuccess = false;
    this.showCustomerTable = false;
    this.b2bBooking.reset();
  }

  openNewAppointmentSection(): void {
    this.showNewAppointmentSection = true;
  }

  closeNewAppointmentSection(): void {
    this.showNewAppointmentSection = false;
    this.selectedTimeSlot = undefined;
    this.selectedCustomer = null;
    this.scheduleSuccess = false;
    this.scheduleError = null;
    this.showCustomerTable = false;
    this.b2bBooking.reset();
  }

  /** Agenda B2B: CanExternalLink na agenda selecionada (ou rota /b2b/appointments). */
  get isB2bBookingFlow(): boolean {
    return this.b2bOnly || !!this.selectedSchedule?.CanExternalLink;
  }

  ngOnDestroy(): void {
    this.b2bBooking.reset();
  }

  // Métodos para gerenciar clientes
  loadCustomers(): void {
    this.loadingCustomers = true;
    this.customersService.getAll({ page: 1, pageSize: 100 }).subscribe({
      next: (customers) => {
        this.customers = customers || [];
        this.loadingCustomers = false;
      },
      error: (err) => {
        console.error('❌ Erro ao carregar clientes:', err);
        this.loadingCustomers = false;
      }
    });
  }

  toggleCustomerTable(): void {
    this.showCustomerTable = !this.showCustomerTable;
    if (this.showCustomerTable && this.customers.length === 0) {
      this.loadCustomers();
    }
  }

  onCustomerSelect(customer: Customer): void {
    this.selectedCustomer = customer;
    this.showCustomerTable = false;
    this.b2bBooking.reset();
    if (this.isB2bBookingFlow && customer?.id) {
      this.b2bBooking.startForCustomer(customer.id);
    }
  }

  clearCustomerSelection(): void {
    this.selectedCustomer = null;
    this.b2bBooking.reset();
  }

  chooseTimeSlot(slot: TimeSlot): void {
    this.selectedTimeSlot = slot;
  }

  scheduleForClient(): void {
    if (!this.selectedPreviewDay || !this.selectedTimeSlot || !this.selectedCustomer || !this.selectedScheduleId) return;

    if (this.isB2bBookingFlow) {
      const b2bError = this.b2bBooking.validateForSubmit();
      if (b2bError) {
        this.scheduleError = b2bError;
        return;
      }
    }
    
    this.creatingAppointment = true;
    this.scheduleSuccess = false;
    this.scheduleError = null;

    const dateStr = this.formatDateToISO(this.selectedPreviewDay.fullDate);
    const documentNumber = (this.selectedCustomer.documentNumber || '').trim();

    if (!documentNumber) {
      this.continueScheduleFlow(dateStr);
      return;
    }

    this.schedulesService.getAppointmentsByDocument(documentNumber, 1, 100).subscribe({
      next: (existingAppointments) => {
        const hasConflict = (existingAppointments || []).some(app => {
          if (app.IsDeleted || app.Status === 'Cancelado') {
            return false;
          }

          const appDate = app.Date.split('T')[0];
          const appStart = app.StartTime.substring(0, 5);
          const appEnd = app.EndTime.substring(0, 5);

          return appDate === dateStr
            && appStart === this.selectedTimeSlot!.start
            && appEnd === this.selectedTimeSlot!.end;
        });

        if (hasConflict) {
          this.scheduleError = 'Este cliente já possui um agendamento neste mesmo horário.';
          this.creatingAppointment = false;
          return;
        }

        this.continueScheduleFlow(dateStr);
      },
      error: (err) => {
        console.error('❌ Erro ao consultar appointments por documento:', err);
        this.scheduleError = 'Não foi possível validar conflitos de horário para este cliente.';
        this.creatingAppointment = false;
      }
    });
  }

  private continueScheduleFlow(dateStr: string): void {
    // Passo 1: Verificar se já existe um TimeSlot para este horário/dia
    this.schedulesService.getAllTimeSlots(this.selectedScheduleId!, dateStr).subscribe({
      next: (existingSlots) => {
        const matchingSlot = existingSlots.find(slot => {
          // Comparar horários normalizando o formato (com ou sem segundos)
          const slotStart = slot.StartTime.substring(0, 5); // "09:00:00" -> "09:00"
          const slotEnd = slot.EndTime.substring(0, 5);
          return slotStart === this.selectedTimeSlot!.start && slotEnd === this.selectedTimeSlot!.end;
        });

        if (matchingSlot) {
          // TimeSlot já existe, criar o appointment diretamente
          this.createAppointment(matchingSlot.Id!, dateStr);
        } else {
          // TimeSlot não existe, criar primeiro
          this.createTimeSlotThenAppointment(dateStr);
        }
      },
      error: (err) => {
        console.error('❌ Erro ao verificar TimeSlots:', err);
        this.scheduleError = 'Erro ao verificar disponibilidade. Tente novamente.';
        this.creatingAppointment = false;
      }
    });
  }

  private createTimeSlotThenAppointment(dateStr: string): void {
    const newTimeSlot: ApiTimeSlot = {
      ScheduleId: this.selectedScheduleId!,
      Date: `${dateStr}T00:00:00`,
      StartTime: `${this.selectedTimeSlot!.start}:00`,
      EndTime: `${this.selectedTimeSlot!.end}:00`,
      Capacity: this.attendancesPerSlot,
      BookedCount: 0
    };

    this.schedulesService.createTimeSlot(newTimeSlot).subscribe({
      next: (createdSlot) => {
        
        if (!createdSlot || !createdSlot.Id) {
          console.error('❌ TimeSlot criado sem Id:', createdSlot);
          this.scheduleError = 'Erro ao criar horário: Id não retornado pela API.';
          this.creatingAppointment = false;
          return;
        }
        
        this.createAppointment(createdSlot.Id, dateStr);
      },
      error: (err) => {
        console.error('❌ Erro ao criar TimeSlot:', err);
        this.scheduleError = 'Erro ao criar horário. Tente novamente.';
        this.creatingAppointment = false;
      }
    });
  }

  private createAppointment(timeSlotId: number, dateStr: string): void {
    const currentUser = this.authService.currentUserValue;
    const userName = currentUser?.username || 'Sistema';

    let newAppointment: Appointment = {
      ScheduleId: this.selectedScheduleId!,
      ScheduleTimeSlotId: timeSlotId,
      Date: `${dateStr}T00:00:00`,
      StartTime: `${this.selectedTimeSlot!.start}:00`,
      EndTime: `${this.selectedTimeSlot!.end}:00`,
      Subject: `${this.selectedCustomer!.name}`,
      Description: `Agendado por ${userName}`,
      CustomerId: this.selectedCustomer!.id!,
      StaffId: 0,
      ServiceOrderId: null,
      Status: 'Agendado',
      IsDeleted: false
    };

    if (this.isB2bBookingFlow) {
      newAppointment = this.b2bBooking.applyMirror(
        newAppointment,
        this.selectedCustomer!.name,
        userName
      );
    }


    this.schedulesService.createAppointment(newAppointment).subscribe({
      next: (createdAppointment) => {
        // Atualizar o BookedCount do TimeSlot
        this.updateTimeSlotBookedCount(timeSlotId);
      },
      error: (err) => {
        console.error('❌ Erro ao criar appointment:', err);
        this.scheduleError = 'Erro ao criar agendamento. Tente novamente.';
        this.creatingAppointment = false;
      }
    });
  }

  private updateTimeSlotBookedCount(timeSlotId: number): void {
    // Buscar o TimeSlot atual para incrementar o BookedCount
    this.schedulesService.getTimeSlotById(timeSlotId).subscribe({
      next: (timeSlot) => {
        const updatedTimeSlot = {
          ...timeSlot,
          BookedCount: timeSlot.BookedCount + 1
        };

        this.schedulesService.updateTimeSlot(timeSlotId, updatedTimeSlot).subscribe({
          next: (updated) => {
            this.creatingAppointment = false;
            this.scheduleSuccess = true;
            
            // Atualizar a interface
            if (this.selectedTimeSlot && this.selectedTimeSlot.capacity !== undefined) {
              this.selectedTimeSlot.capacity = updated.Capacity - updated.BookedCount;
              this.selectedTimeSlot.bookedCount = updated.BookedCount;
              this.selectedTimeSlot.totalCapacity = updated.Capacity;
            }
            
            // Recarregar o dia e mostrar modal de sucesso
            this.reloadCurrentDay();
            this.showSuccessModal = true;
          },
          error: (err) => {
            console.error('❌ Erro ao atualizar TimeSlot:', err);
            this.creatingAppointment = false;
            // Mesmo com erro na atualização, o appointment foi criado
            this.scheduleSuccess = true;
            this.reloadCurrentDay();
            this.showSuccessModal = true;
          }
        });
      },
      error: (err) => {
        console.error('❌ Erro ao buscar TimeSlot:', err);
        this.creatingAppointment = false;
        // Mesmo com erro na atualização, o appointment foi criado
        this.scheduleSuccess = true;
      }
    });
  }

  prevWeek(): void {
    this.weeklyStartDate = this.addDays(this.weeklyStartDate, -7);
    this.buildWeeklyPreview();
  }

  nextWeek(): void {
    this.weeklyStartDate = this.addDays(this.weeklyStartDate, 7);
    this.buildWeeklyPreview();
  }

  // Navegação mensal
  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear -= 1;
    } else {
      this.viewMonth -= 1;
    }
    this.buildMonthlyPreview();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear += 1;
    } else {
      this.viewMonth += 1;
    }
    this.buildMonthlyPreview();
  }

  onYearChange(year: number): void {
    this.viewYear = Number(year);
    this.buildMonthlyPreview();
  }

  private buildMonthlyPreview(): void {
    const year = this.viewYear;
    const month = this.viewMonth;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas da data

    const result: CalendarDay[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const weekday = date.getDay(); // 0=Sun, 6=Sat
      
      // Verificar se o dia está excluído
      const isExcluded = this.isDayExcluded(date);
      
      // Verificar se a data é retroativa
      const isPastDate = date < today;
      
      let available = this.isWeekdayAvailable(weekday);
      let reason: Reason = undefined;
      
      if (isPastDate) {
        available = false;
        reason = 'excluded';
      } else if (isExcluded) {
        available = false;
        reason = 'excluded';
      } else if (!available) {
        reason = 'weekday';
      }
      
      result.push({
        dayName: this.getWeekdayShort(date),
        dayNumber: d,
        monthName: this.getMonthName(month),
        monthShort: this.getMonthShortName(month),
        fullDate: date,
        available,
        reason,
        timeSlots: available ? this.generateTimeSlots() : [],
        appointmentsCount: this.getAppointmentsCountForDate(date)
      });
    }
    this.previewDays = result;
    if (this.showCalendar) {
      this.selectedPreviewDay = undefined;
    }
  }

  private initWeeklyView(): void {
    const today = new Date();
    const monday = this.getStartOfWeek(today);
    this.weeklyStartDate = monday;
    this.buildWeeklyPreview();
  }

  // Dashboard
  private loadDashboard(): void {
    if (!this.selectedScheduleId) {
      this.dashboardError = 'Selecione uma agenda para visualizar o Dashboard.';
      return;
    }

    this.dashboardLoading = true;
    this.dashboardError = null;

    this.schedulesService.getAppointmentsDashboard(this.selectedScheduleId).subscribe({
      next: (data) => {
        try {
          this.dashConfig = this.buildDashConfigFromDashboard(data);
        } catch (e) {
          console.error('Erro ao montar config do dashboard:', e);
          this.dashboardError = 'Erro ao montar os gráficos do dashboard.';
        } finally {
          this.dashboardLoading = false;
        }
      },
      error: (err) => {
        console.error('❌ Erro ao carregar dashboard:', err);
        this.dashboardError = 'Não foi possível carregar os dados do dashboard.';
        this.dashboardLoading = false;
      }
    });
  }

  private buildDashConfigFromDashboard(data: AppointmentsDashboard): DashComponentConfig {
    const statusMap: { [key: string]: number } = {};
    (data.byStatus || []).forEach(s => {
      statusMap[s.status] = s.count;
    });

    const total = data.totalAppointments || 0;
    const agendado = statusMap['Agendado'] || 0;
    const confirmado = statusMap['Confirmado'] || 0;
    const cancelado = statusMap['Cancelado'] || 0;

    const firstRowCards: DashCard[] = [
      { title: 'Total de Agendamentos', value: total, icon: 'fas fa-list', color: 'primary' },
      { title: 'Agendado', value: agendado, icon: 'fas fa-calendar-check', color: 'success' },
      { title: 'Confirmado', value: confirmado, icon: 'fas fa-check-circle', color: 'info' },
      { title: 'Cancelado', value: cancelado, icon: 'fas fa-times-circle', color: 'danger' }
    ];

    const firstRowChart: DashChart = {
      type: 'doughnut',
      title: 'Distribuição por Status',
      data: {
        labels: (data.byStatus || []).map(s => s.status),
        datasets: [
          {
            label: 'Agendamentos',
            data: (data.byStatus || []).map(s => s.count),
            backgroundColor: ['#198754', '#dc3545', '#0dcaf0', '#6c757d'],
            borderColor: '#fff',
            borderWidth: 1
          }
        ]
      }
    };

    const topItems = data.topDays?.items || [];
    const labels = topItems.map(i => this.formatDashboardDateLabel(i.date));
    const counts = topItems.map(i => i.count);

    const secondRowChart: DashChart = {
      type: 'bar',
      title: 'Dias com mais agendamentos',
      data: {
        labels,
        datasets: [
          {
            label: 'Qtd por Dia',
            data: counts,
            backgroundColor: '#3A4D56',
            borderColor: '#3A4D56',
            borderWidth: 1
          }
        ]
      },
      options: {
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } }
        }
      }
    };

    const scheduleName = this.selectedSchedule?.Name || `Agenda #${data.scheduleId}`;
    const secondRowCards: DashCard[] = [
      { title: 'Agenda', value: scheduleName, icon: 'fas fa-calendar-alt', color: 'info' }
    ];

    return {
      firstRowCards,
      firstRowChart,
      secondRowChart,
      secondRowCards
    };
  }

  private formatDashboardDateLabel(dateStr: string): string {
    // Espera formato ISO com T00:00:00
    const d = new Date(dateStr);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${dd}/${mm}`;
  }

  private buildWeeklyPreview(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Zerar horas para comparação apenas da data
    
    const days: CalendarDay[] = [];
    for (let i = 0; i < 7; i++) {
      const date = this.addDays(this.weeklyStartDate, i);
      const weekday = date.getDay();
      
      // Verificar se o dia está excluído
      const isExcluded = this.isDayExcluded(date);
      
      // Verificar se a data é retroativa
      const isPastDate = date < today;
      
      let available = this.isWeekdayAvailable(weekday);
      let reason: Reason = undefined;
      
      if (isPastDate) {
        available = false;
        reason = 'excluded';
      } else if (isExcluded) {
        available = false;
        reason = 'excluded';
      } else if (!available) {
        reason = 'weekday';
      }
      
      days.push({
        dayName: this.getWeekdayShort(date),
        dayNumber: date.getDate(),
        monthName: this.getMonthName(date.getMonth()),
        monthShort: this.getMonthShortName(date.getMonth()),
        fullDate: date,
        available,
        reason,
        timeSlots: available ? this.generateTimeSlots() : [],
        appointmentsCount: this.getAppointmentsCountForDate(date)
      });
    }
    this.weeklyDays = days;
  }

  private generateTimeSlots(): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const intervalMinutes = this.selectedSchedule?.TimeIntervalMinutes ?? 30;
    let start = new Date();
    start.setHours(8, 0, 0, 0);
    const endOfDay = new Date();
    endOfDay.setHours(18, 0, 0, 0);

    while (start < endOfDay) {
      const end = new Date(start.getTime() + intervalMinutes * 60000);
      slots.push({
        start: this.formatTime(start),
        end: this.formatTime(end),
        capacity: this.selectedSchedule?.AppointmentsByHours ?? this.attendancesPerSlot
      });
      start = end;
    }
    return slots;
  }

  private isWeekdayAvailable(jsDay: number): boolean {
    if (!this.selectedSchedule) {
      return jsDay !== 0 && jsDay !== 6; // padrão seg-sex
    }
    const s = this.selectedSchedule;
    const map = [s.Sunday, s.Monday, s.Tuesday, s.Wednesday, s.Thursday, s.Friday, s.Saturday];
    return !!map[jsDay];
  }

  private isDayExcluded(date: Date): boolean {
    if (!this.excludedDays || this.excludedDays.length === 0) {
      return false;
    }
    
    const dateStr = this.formatDateToISO(date);
    return this.excludedDays.some(ed => {
      const excludedDateStr = ed.Date.split('T')[0];
      return excludedDateStr === dateStr;
    });
  }

  private formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseApiDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const datePart = value.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private focusCalendarOnDate(targetDate: Date): void {
    this.previewMode = 'monthly';
    this.showCalendar = true;
    this.showTimeSlots = false;
    this.viewYear = targetDate.getFullYear();
    this.viewMonth = targetDate.getMonth();
    this.buildMonthlyPreview();

    const targetDateIso = this.formatDateToISO(targetDate);
    const selectedDay = this.previewDays.find(day => this.formatDateToISO(day.fullDate) === targetDateIso);

    if (!selectedDay) {
      this.searchByDocumentError = 'Dia encontrado, mas não foi possível abrir no calendário.';
      return;
    }

    this.closeSearchModal();
    this.selectPreviewDay(selectedDay);
  }

  private tryHandlePendingSearchNavigation(): void {
    if (!this.pendingSearchNavigationDate) {
      return;
    }

    const targetDate = this.pendingSearchNavigationDate;
    this.pendingSearchNavigationDate = null;
    this.focusCalendarOnDate(targetDate);
  }

  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  private getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = (day + 6) % 7; // transformar para 0=Segunda
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  getMonthName(monthIndex: number): string {
    const months = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
    return months[monthIndex] || '';
  }

  private getMonthShortName(monthIndex: number): string {
    const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
    return months[monthIndex] || '';
  }

  private getWeekdayShort(date: Date): string {
    const map = ['DOM','SEG','TER','QUA','QUI','SEX','SÁB'];
    return map[date.getDay()];
  }

  private formatTime(date: Date): string {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }

  private initYearsList(): void {
    const base = new Date().getFullYear();
    const start = base - 5;
    const end = base + 5;
    const arr: number[] = [];
    for (let y = start; y <= end; y++) arr.push(y);
    this.yearsList = arr;
  }

  // Métodos para gerenciar appointments do dia
  private loadDayAppointments(date: Date): void {
    this.loadingAppointments = true;
    const dateStr = this.formatDateToISO(date);

    const schedule$ = this.selectedScheduleId
      ? this.schedulesService.getAppointmentsByScheduleId(this.selectedScheduleId).pipe(
          map(list => (list || []).filter(app => {
            if (app.IsDeleted) return false;
            const appDate = String(app.Date || '').split('T')[0];
            return !!appDate && appDate === dateStr;
          })),
          catchError(err => {
            console.error('❌ Erro ao carregar appointments da agenda:', err);
            return of([] as Appointment[]);
          })
        )
      : of([] as Appointment[]);

    const partners$ = this.schedulesService.getPartnerAppointmentsByDate(dateStr).pipe(
      catchError(err => {
        console.error('❌ Erro ao carregar appointments parceiros:', err);
        return of([] as Appointment[]);
      })
    );

    forkJoin({ schedule: schedule$, partners: partners$ }).subscribe({
      next: ({ schedule, partners }) => {
        const byId = new Map<number, Appointment>();
        [...schedule, ...partners].forEach(app => {
          if (app.IsDeleted) return;
          if (app.Id) {
            byId.set(app.Id, app);
          } else {
            byId.set(-byId.size - 1, app);
          }
        });

        this.dayAppointments = Array.from(byId.values()).sort((a, b) =>
          String(a.StartTime || '').localeCompare(String(b.StartTime || ''))
        );
        this.appointmentsPage = 1;
        this.loadingAppointments = false;
      },
      error: (err) => {
        console.error('❌ Erro ao carregar appointments:', err);
        this.dayAppointments = [];
        this.loadingAppointments = false;
      }
    });
  }

  deleteAppointment(appointment: Appointment): void {
    if (!appointment.Id) return;

    if (!confirm(`Deseja realmente excluir o agendamento de ${appointment.Subject}?`)) {
      return;
    }

    this.deletingAppointmentId = appointment.Id;
    const deletedBy = this.authService.getUserNameFromToken?.()
      || this.authService.currentUserValue?.username
      || 'Operação';

    this.schedulesService.deleteAppointment(appointment.Id, deletedBy).subscribe({
      next: () => {
        
        // Decrementar o BookedCount do TimeSlot correspondente
        if (appointment.ScheduleTimeSlotId) {
          this.decrementTimeSlotBookedCount(appointment.ScheduleTimeSlotId);
        }
        
        // Recarregar appointments do dia
        if (this.selectedPreviewDay) {
          this.loadDayAppointments(this.selectedPreviewDay.fullDate);
        }

        if (this.selectedScheduleId) {
          this.loadScheduleAppointmentsCount(this.selectedScheduleId);
        }
        
        this.deletingAppointmentId = null;
      },
      error: (err) => {
        console.error('❌ Erro ao excluir appointment:', err);
        this.showInfoMessage('Erro ao excluir agendamento. Tente novamente.', 'Erro', 'danger');
        this.deletingAppointmentId = null;
      }
    });
  }

  openAppointmentDetailsModal(appointment: Appointment): void {
    this.selectedAppointmentDetails = appointment;
    this.showAppointmentDetailsModal = true;
    this.appointmentHistory = [];
    this.loadingAppointmentHistory = true;
    if (!appointment?.Id) {
      this.loadingAppointmentHistory = false;
      return;
    }
    this.schedulesService.getAppointmentHistory(appointment.Id).subscribe({
      next: (list) => {
        this.appointmentHistory = list || [];
        this.loadingAppointmentHistory = false;
      },
      error: () => {
        this.appointmentHistory = [];
        this.loadingAppointmentHistory = false;
      }
    });
  }

  closeAppointmentDetailsModal(): void {
    this.showAppointmentDetailsModal = false;
    this.selectedAppointmentDetails = null;
    this.appointmentHistory = [];
  }

  generateServiceOrder(appointment: Appointment): void {
    if (!appointment?.Id) {
      this.showInfoMessage('Agendamento sem identificador para gerar OS.', 'Aviso', 'warning');
      return;
    }
    this.router.navigate(['/appointments', appointment.Id, 'generate-os']);
  }

  /** Após comparecimento: abre criação de orçamento B2B (OS + budget) pré-preenchida. */
  createBudgetFromAppointment(appointment: Appointment): void {
    if (!appointment?.Id) {
      this.showInfoMessage('Agendamento sem identificador.', 'Aviso', 'warning');
      return;
    }
    if (!appointment.B2BContractId) {
      this.showInfoMessage('Agendamento sem contrato B2B. Use Gerar OS ou vincule o contrato.', 'Aviso', 'warning');
      return;
    }
    const plate = resolveAppointmentPlate(appointment) || (appointment.Plate || '').trim().toUpperCase();
    const fromGuincho = this.isGuinchoSolicitacao(appointment);
    this.router.navigate(['/b2b/budget-service-orders/novo'], {
      queryParams: {
        appointmentId: appointment.Id,
        b2bContractId: appointment.B2BContractId,
        plate: plate || undefined,
        vehicleId: appointment.VehicleId || undefined,
        customerId: appointment.CustomerId || undefined,
        driverName: appointment.DriverName || undefined,
        externalServiceProviderId: appointment.ExternalServiceProviderId || undefined,
        externalServiceProviderName: appointment.ExternalServiceProviderName || undefined,
        isPartnerAppointment: (appointment.ExternalServiceProviderId ?? 0) > 0 || fromGuincho
          ? 'true'
          : undefined,
        fromGuincho: fromGuincho ? 'true' : undefined
      }
    });
  }

  canCreateBudgetFromAppointment(app: Appointment): boolean {
    return !!(
      this.isB2bAttendanceEligible(app)
      && app.Status === 'Compareceu'
      && !app.ServiceOrderId
      && app.B2BContractId
    );
  }

  /**
   * Trava apenas comparecimento final / OS gerada.
   * Confirmado pode ser corrigido pela operação (exceto veículo).
   */
  isAttendanceLocked(app: Appointment): boolean {
    return app.Status === 'Compareceu'
      || app.Status === 'Não compareceu'
      || app.Status === 'Gerado OS';
  }

  /** Pode reagendar / trocar oficina / status — não o veículo. */
  canEditConfirmedAppointment(app: Appointment): boolean {
    return !this.isAttendanceLocked(app) && !app.IsDeleted;
  }

  private decrementTimeSlotBookedCount(timeSlotId: number): void {
    this.schedulesService.getTimeSlotById(timeSlotId).subscribe({
      next: (timeSlot) => {
        const updatedTimeSlot = {
          ...timeSlot,
          BookedCount: Math.max(0, timeSlot.BookedCount - 1)
        };

        this.schedulesService.updateTimeSlot(timeSlotId, updatedTimeSlot).subscribe({
          next: (updated) => {
            
            // Recarregar disponibilidade do dia se necessário
            if (this.selectedPreviewDay) {
              this.selectPreviewDay(this.selectedPreviewDay);
            }
          },
          error: (err) => {
            console.error('❌ Erro ao atualizar TimeSlot:', err);
          }
        });
      },
      error: (err) => {
        console.error('❌ Erro ao buscar TimeSlot:', err);
      }
    });
  }

  // Métodos de paginação dos appointments
  nextAppointmentsPage(): void {
    if (this.appointmentsPage < this.totalAppointmentsPages) {
      this.appointmentsPage++;
    }
  }

  prevAppointmentsPage(): void {
    if (this.appointmentsPage > 1) {
      this.appointmentsPage--;
    }
  }

  goToAppointmentsPage(page: number): void {
    if (page >= 1 && page <= this.totalAppointmentsPages) {
      this.appointmentsPage = page;
    }
  }

  // Recarregar o dia atual após criar/excluir appointment
  private reloadCurrentDay(): void {
    if (this.selectedScheduleId) {
      this.loadScheduleAppointmentsCount(this.selectedScheduleId);
    }

    if (this.selectedPreviewDay) {
      // Recarregar appointments
      this.loadDayAppointments(this.selectedPreviewDay.fullDate);
      
      // Recarregar disponibilidade de horários
      if (this.selectedScheduleId && this.selectedPreviewDay.available) {
        const dateStr = this.formatDateToISO(this.selectedPreviewDay.fullDate);
        this.schedulesService.getAllTimeSlots(this.selectedScheduleId, dateStr).subscribe({
          next: (existingSlots) => {
            this.updateDayTimeSlots(existingSlots);
          },
          error: (err) => {
            console.error('❌ Erro ao recarregar TimeSlots:', err);
          }
        });
      }
    }
  }

  private updateDayTimeSlots(existingSlots: any[]): void {
    if (!this.selectedPreviewDay) return;
    
    if (existingSlots && existingSlots.length > 0) {
      this.selectedPreviewDay.timeSlots = this.selectedPreviewDay.timeSlots.map(generatedSlot => {
        const matchingSlot = existingSlots.find(apiSlot => {
          const slotStart = apiSlot.StartTime.substring(0, 5);
          const slotEnd = apiSlot.EndTime.substring(0, 5);
          return slotStart === generatedSlot.start && slotEnd === generatedSlot.end;
        });
        
        if (matchingSlot) {
          const availableCapacity = matchingSlot.Capacity - matchingSlot.BookedCount;
          return {
            ...generatedSlot,
            capacity: availableCapacity,
            bookedCount: matchingSlot.BookedCount,
            totalCapacity: matchingSlot.Capacity
          };
        }
        
        return generatedSlot;
      });
      
      this.selectedPreviewDay.timeSlots = this.selectedPreviewDay.timeSlots.filter(slot => 
        slot.capacity === undefined || slot.capacity > 0
      );
    }
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.selectedCustomer = null;
    this.b2bBooking.reset();
    this.reloadCurrentDay();
  }

  // Métodos para gerenciar link externo
  openExternalLinkModal(): void {
    if (!this.selectedScheduleId) {
      return;
    }

    this.showExternalLinkModal = true;
    this.generatedExternalLink = null;
    this.generatingLink = false;
    this.linkSelectedCustomerId = null;
    this.linkSelectedProviderId = null;

    if (this.customers.length === 0) {
      this.loadCustomers();
    }
    this.loadProvidersForLink();
  }

  private loadProvidersForLink(): void {
    this.loadingProviders = true;
    this.externalServiceProvidersService.list(1, 100).subscribe({
      next: (res) => {
        this.providers = res.items || [];
        this.loadingProviders = false;
      },
      error: (err) => {
        console.error('❌ Erro ao carregar prestadores:', err);
        this.providers = [];
        this.loadingProviders = false;
      }
    });
  }

  generateExternalLink(): void {
    if (!this.selectedScheduleId || !this.linkSelectedCustomerId || !this.linkSelectedProviderId) {
      return;
    }

    this.generatingLink = true;
    this.generatedExternalLink = null;

    const userId = this.authService.getUserIdFromToken() || '';
    const payload: ExternalLinkCreate = {
      createdBy: userId,
      updatedBy: userId
    };

    this.externalLinksService.create(payload).subscribe({
      next: (link) => {
        const baseUrl = window.location.origin;
        this.generatedExternalLink =
          `${baseUrl}/appointments-external/${link.code}` +
          `?clientId=${this.linkSelectedCustomerId}` +
          `&providerId=${this.linkSelectedProviderId}` +
          `&scheduleId=${this.selectedScheduleId}`;
        this.generatingLink = false;
      },
      error: () => {
        this.generatingLink = false;
      }
    });
  }

  closeExternalLinkModal(): void {
    this.showExternalLinkModal = false;
    this.generatedExternalLink = null;
    this.linkSelectedCustomerId = null;
    this.linkSelectedProviderId = null;
  }

  copyExternalLink(): void {
    if (this.generatedExternalLink) {
      navigator.clipboard.writeText(this.generatedExternalLink).then(() => {
        // Maybe show a toast message for success
      });
    }
  }

  // Métodos para gerenciar status dos appointments
  openStatusSelectionModal(appointment: Appointment): void {
    if (!appointment.Id) {
      return;
    }

    this.selectedAppointmentForStatus = appointment;
    this.showStatusSelectionModal = true;
  }

  closeStatusSelectionModal(): void {
    this.showStatusSelectionModal = false;
    this.selectedAppointmentForStatus = null;
  }

  selectStatusFromModal(newStatus: string): void {
    if (!this.selectedAppointmentForStatus) {
      return;
    }

    const appointment = this.selectedAppointmentForStatus;
    this.closeStatusSelectionModal();
    this.updateAppointmentStatus(appointment, newStatus);
  }

  updateAppointmentStatus(appointment: Appointment, newStatus: string): void {
    if (!appointment.Id || appointment.Status === newStatus) {
      return;
    }

    // Se o status for "Cancelado", abrir modal para solicitar motivo
    if (newStatus === 'Cancelado') {
      this.cancelReasonMode = 'cancel';
      this.pendingStatusUpdate = { appointment, status: newStatus };
      this.cancelReason = '';
      this.cancelReasonError = '';
      this.showCancelReasonModal = true;
      return;
    }

    if (newStatus === 'Compareceu' || newStatus === 'Não compareceu') {
      this.pendingAttendanceUpdate = { appointment, status: newStatus };
      this.attendanceRecordedAt = defaultDatetimeLocalValue();
      this.attendanceRecordedAtError = '';
      this.showAttendanceRecordedAtModal = true;
      return;
    }

    // Para outros status, proceder normalmente
    this.performStatusUpdate(appointment, newStatus, '');
  }

  confirmCancelReason(): void {
    // Validar motivo (mínimo 5 caracteres)
    if (this.cancelReason.trim().length < 5) {
      this.cancelReasonError = 'O motivo deve ter no mínimo 5 caracteres.';
      return;
    }

    this.cancelReasonError = '';
    this.showCancelReasonModal = false;

    if (this.pendingStatusUpdate) {
      if (this.cancelReasonMode === 'reject-partner') {
        this.executePartnerAnalysis(
          this.pendingStatusUpdate.appointment,
          'Cancelado',
          this.cancelReason.trim()
        );
      } else {
        this.performStatusUpdate(
          this.pendingStatusUpdate.appointment,
          this.pendingStatusUpdate.status,
          this.cancelReason.trim()
        );
      }
      this.pendingStatusUpdate = null;
      this.cancelReasonMode = 'cancel';
    }
  }

  closeCancelReasonModal(): void {
    this.showCancelReasonModal = false;
    this.cancelReason = '';
    this.cancelReasonError = '';
    this.pendingStatusUpdate = null;
    this.cancelReasonMode = 'cancel';
  }

  closeAttendanceRecordedAtModal(): void {
    this.showAttendanceRecordedAtModal = false;
    this.attendanceRecordedAt = '';
    this.attendanceRecordedAtError = '';
    this.pendingAttendanceUpdate = null;
  }

  confirmAttendanceRecordedAt(): void {
    const iso = datetimeLocalToRecordedAtIso(this.attendanceRecordedAt);
    if (!iso) {
      this.attendanceRecordedAtError = 'Informe uma data e hora válidas.';
      return;
    }

    this.attendanceRecordedAtError = '';
    this.showAttendanceRecordedAtModal = false;

    if (this.pendingAttendanceUpdate) {
      this.performStatusUpdate(
        this.pendingAttendanceUpdate.appointment,
        this.pendingAttendanceUpdate.status,
        '',
        iso
      );
      this.pendingAttendanceUpdate = null;
    }
  }

  private performStatusUpdate(
    appointment: Appointment,
    newStatus: string,
    reason: string,
    attendedAt?: string | null
  ): void {
    const currentUser = this.authService.currentUserValue;
    const userName = currentUser?.username || 'Sistema';

    this.updatingStatusAppointmentId = appointment.Id!;

    // Construir descrição
    let description = '';
    if (reason) {
      description = `(${reason}) - Alterado por ${userName}`;
    } else {
      description = `Alterado por ${userName}`;
    }

    const updatedAppointment = {
      ...appointment,
      Status: newStatus,
      Description: description,
      UpdatedBy: userName,
      AttendedAt: attendedAt ?? appointment.AttendedAt ?? null
    };


    this.schedulesService.updateAppointment(appointment.Id!, updatedAppointment).subscribe({
      next: (updated) => {
        this.updatingStatusAppointmentId = null;
        
        // Recarregar appointments do dia
        if (this.selectedPreviewDay) {
          this.loadDayAppointments(this.selectedPreviewDay.fullDate);
        }

        if (this.selectedScheduleId) {
          this.loadScheduleAppointmentsCount(this.selectedScheduleId);
        }

        if (this.b2bOnly) {
          this.loadPartnerPendingAppointments();
        }
      },
      error: (err) => {
        console.error('❌ Erro ao atualizar status:', err);
        this.showInfoMessage('Erro ao atualizar status. Tente novamente.', 'Erro', 'danger');
        this.updatingStatusAppointmentId = null;
      }
    });
  }
}
